import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Reserved subdomains that never map to a tenant — platform-level infra.
// Must match the set in backend/app/middleware/tenant.py.
const RESERVED_SLUGS = new Set(['www', 'api', 'cdn', 'inbde']);

/**
 * Derive the tenant slug from the current browser host.
 *
 * Recognized suffixes:
 *   - <slug>.withvidya.ai  (prod + staging)
 *   - <slug>.localtest.me  (local dev — *.localtest.me resolves to 127.0.0.1
 *                           via public DNS if your resolver honors it)
 *   - <slug>.localhost     (local dev — Chrome/Firefox treat *.localhost as
 *                           127.0.0.1 per RFC 6761, zero DNS dependency)
 *
 * Returns null for platform requests (bare withvidya.ai, bare localhost,
 * reserved slugs like www/api/cdn, or any unrecognized host such as raw IPs).
 *
 * The value is a public identifier (already in the URL bar) and is only a
 * hint to the backend — every tenant-scoped access decision independently
 * verifies InstitutionMembership server-side.
 */
export function getTenantSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  for (const suffix of ['.withvidya.ai', '.localtest.me', '.localhost']) {
    if (host.endsWith(suffix)) {
      const slug = host.slice(0, -suffix.length);
      if (!slug || RESERVED_SLUGS.has(slug)) return null;
      return slug;
    }
  }
  return null;
}

/** Brands served from this same bundle. `inbde` is a reserved slug, so tenant
 * resolution stays inert on that host and no institution is ever looked up. */
export type Brand = 'vidya' | 'inbde';

export function getBrand(): Brand {
  if (process.env.REACT_APP_BRAND === 'inbde') return 'inbde';
  if (typeof window === 'undefined') return 'vidya';
  const host = window.location.hostname;
  for (const suffix of ['.withvidya.ai', '.localtest.me', '.localhost']) {
    if (host.endsWith(suffix) && host.slice(0, -suffix.length) === 'inbde') return 'inbde';
  }
  return 'vidya';
}

/**
 * Headers to merge into bare `fetch()` calls that hit our API. The axios
 * instance below already sets these via the request interceptor; use this
 * helper for non-axios calls (e.g., SSE streaming fetches).
 */
export function tenantHeaders(): Record<string, string> {
  const slug = getTenantSlug();
  return slug ? { 'X-Vidya-Tenant': slug } : {};
}

/**
 * Inverse of `getTenantSlug` — builds a same-protocol, same-port URL that
 * targets the given tenant subdomain on the matching base domain. Used by
 * InstitutionRoute to redirect institute-management traffic onto the
 * tenant subdomain when REACT_APP_RESTRICT_INSTITUTE_MGMT_TO_SUBDOMAIN is on.
 *
 * Strategy: detect the recognized base from the current host (`.withvidya.ai`,
 * `.localtest.me`, or `.localhost`) and prefix the slug. If the host is a
 * raw IP or unrecognized, return null — caller should not redirect.
 */
/**
 * Build a same-protocol, same-port URL that targets the bare platform host
 * (no tenant subdomain). Counterpart to `buildTenantUrl`. Returns null if
 * the current host is unrecognized (raw IP, dev box) so the caller can
 * skip the redirect rather than wedging the user.
 */
export function buildPlatformUrl(pathAndSearch: string): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  const portSuffix = port ? `:${port}` : '';

  const bases = ['withvidya.ai', 'localtest.me', 'localhost'];
  for (const base of bases) {
    if (host === base || host.endsWith(`.${base}`)) {
      return `${protocol}//${base}${portSuffix}${pathAndSearch}`;
    }
  }
  return null;
}

export function buildTenantUrl(slug: string, pathAndSearch: string): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  const portSuffix = port ? `:${port}` : '';

  // Recognized bases (in priority order). Strip an existing slug prefix
  // before re-prefixing so this is idempotent on already-tenant URLs.
  const bases = ['withvidya.ai', 'localtest.me', 'localhost'];
  for (const base of bases) {
    // Either we're on the bare base, or on `<existing-slug>.<base>`.
    if (host === base || host.endsWith(`.${base}`)) {
      return `${protocol}//${slug}.${base}${portSuffix}${pathAndSearch}`;
    }
  }
  return null;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach auth token + tenant slug to every request.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const slug = getTenantSlug();
    if (slug && config.headers) {
      config.headers['X-Vidya-Tenant'] = slug;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = /^\/inbde(?:\/|\?|$)/.test(currentPath)
        ? `/login?next=${encodeURIComponent(currentPath)}` : '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
